<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import maxChannel from 'dashboard/api/channel/maxChannel';

const props = defineProps({
  inboxId: {
    type: Number,
    required: true,
  },
  providerConfig: {
    type: Object,
    default: () => ({}),
  },
  phoneNumber: {
    type: String,
    default: '',
  },
  showTitle: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits(['connected', 'disconnected', 'statusChange']);

const { t } = useI18n();
const store = useStore();

const session = ref({
  session_id: props.providerConfig?.session_id || null,
  status: props.providerConfig?.session_status || 'not_started',
  account_name: props.providerConfig?.connected_account_name || null,
  phone_number: props.phoneNumber || null,
  last_error: props.providerConfig?.session_error || null,
});

const code = ref('');
const password = ref('');
const isLoading = ref(false);
let pollTimer = null;

const POLLING_STATUSES = [
  'starting',
  'reconnecting',
  'awaiting_code',
  'awaiting_password',
];

const applySession = nextSession => {
  session.value = {
    session_id: nextSession?.session_id || session.value.session_id,
    status: nextSession?.status || 'not_started',
    account_name: nextSession?.account_name || '',
    phone_number: nextSession?.phone_number || session.value.phone_number,
    last_error: nextSession?.last_error || '',
  };
};

const syncInboxes = async () => {
  await store.dispatch('inboxes/get');
};

const handleStatusTransition = async previousStatus => {
  if (previousStatus === session.value.status) {
    return;
  }

  const nextStatus = session.value.status;

  if (['connected', 'disconnected'].includes(nextStatus)) {
    await syncInboxes();
  }

  emit('statusChange', session.value);

  if (nextStatus === 'connected') {
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.MAX.CONNECT_SUCCESS'));
    emit('connected', session.value);
  } else if (nextStatus === 'disconnected') {
    emit('disconnected', session.value);
  }
};

const fetchSession = async ({ create = false, silent = false } = {}) => {
  isLoading.value = !silent;

  try {
    const response = create
      ? await maxChannel.createSession({ inboxId: props.inboxId })
      : await maxChannel.getSession(props.inboxId);

    const previousStatus = session.value.status;
    applySession(response.data.session);
    await handleStatusTransition(previousStatus);
  } catch (error) {
    if (!silent) {
      useAlert(
        error.message || t('INBOX_MGMT.SETTINGS_POPUP.MAX.LOAD_ERROR')
      );
    }
  } finally {
    isLoading.value = false;
  }
};

const submitCode = async () => {
  if (!code.value.trim()) {
    return;
  }

  isLoading.value = true;

  try {
    const previousStatus = session.value.status;
    const response = await maxChannel.submitCode({
      inboxId: props.inboxId,
      code: code.value.trim(),
    });
    applySession(response.data.session);
    code.value = '';
    await handleStatusTransition(previousStatus);
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.SETTINGS_POPUP.MAX.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const submitPassword = async () => {
  if (!password.value.trim()) {
    return;
  }

  isLoading.value = true;

  try {
    const previousStatus = session.value.status;
    const response = await maxChannel.submitPassword({
      inboxId: props.inboxId,
      password: password.value.trim(),
    });
    applySession(response.data.session);
    password.value = '';
    await handleStatusTransition(previousStatus);
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.SETTINGS_POPUP.MAX.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const disconnectSession = async () => {
  isLoading.value = true;

  try {
    const previousStatus = session.value.status;
    const response = await maxChannel.deleteSession({
      inboxId: props.inboxId,
    });
    applySession(response.data.session);
    await handleStatusTransition(previousStatus);
    useAlert(t('INBOX_MGMT.SETTINGS_POPUP.MAX.DISCONNECT_SUCCESS'));
  } catch (error) {
    useAlert(error.message || t('INBOX_MGMT.SETTINGS_POPUP.MAX.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const statusLabel = computed(() => {
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.MAX.STATUS.${session.value.status?.toUpperCase?.() || 'NOT_STARTED'}`
  );
});

const statusDescription = computed(() => {
  return t(
    `INBOX_MGMT.SETTINGS_POPUP.MAX.STATUS_HELP.${session.value.status?.toUpperCase?.() || 'NOT_STARTED'}`
  );
});

const statusClass = computed(() => {
  const variants = {
    connected: 'bg-n-teal-3 text-n-teal-11',
    starting: 'bg-n-blue-3 text-n-blue-11',
    reconnecting: 'bg-n-blue-3 text-n-blue-11',
    awaiting_code: 'bg-n-amber-3 text-n-amber-11',
    awaiting_password: 'bg-n-amber-3 text-n-amber-11',
    disconnected: 'bg-n-slate-3 text-n-slate-11',
    error: 'bg-n-ruby-3 text-n-ruby-11',
    not_started: 'bg-n-slate-3 text-n-slate-11',
  };

  return variants[session.value.status] || variants.not_started;
});

const shouldPoll = computed(() => POLLING_STATUSES.includes(session.value.status));

const primaryActionLabel = computed(() => {
  if (session.value.status === 'connected') {
    return t('INBOX_MGMT.SETTINGS_POPUP.MAX.REFRESH_BUTTON');
  }

  return t('INBOX_MGMT.SETTINGS_POPUP.MAX.CONNECT_BUTTON');
});

const showDisconnectButton = computed(() => {
  return [
    'connected',
    'starting',
    'reconnecting',
    'awaiting_code',
    'awaiting_password',
  ].includes(session.value.status);
});

const schedulePoll = () => {
  clearTimeout(pollTimer);
  if (!shouldPoll.value) return;

  pollTimer = window.setTimeout(() => {
    fetchSession({ silent: true });
  }, 3000);
};

watch(
  () => props.providerConfig,
  providerConfig => {
    applySession({
      session_id: providerConfig?.session_id,
      status: providerConfig?.session_status,
      account_name: providerConfig?.connected_account_name,
      last_error: providerConfig?.session_error,
      phone_number: props.phoneNumber,
    });
  },
  { deep: true }
);

watch(
  () => session.value.status,
  () => {
    schedulePoll();
  },
  { immediate: true }
);

onMounted(() => {
  fetchSession({ silent: true });
});

onUnmounted(() => {
  clearTimeout(pollTimer);
});
</script>

<template>
  <div class="rounded-2xl border border-n-weak bg-n-alpha-2 p-5">
    <div
      class="flex flex-col gap-4 md:flex-row md:items-start md:justify-between"
    >
      <div class="min-w-0 flex-1">
        <div v-if="showTitle">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.TITLE') }}
          </h3>
          <p class="mt-1 text-sm text-n-slate-11">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.SUBHEADER') }}
          </p>
        </div>

        <div class="mt-4 flex flex-wrap items-center gap-2">
          <span
            class="inline-flex rounded-full px-3 py-1 text-xs font-medium"
            :class="statusClass"
          >
            {{ statusLabel }}
          </span>
          <span class="text-sm text-n-slate-11">
            {{ statusDescription }}
          </span>
        </div>

        <dl class="mt-4 grid gap-3 text-sm text-n-slate-11 md:grid-cols-2">
          <div>
            <dt class="font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.PHONE_NUMBER') }}
            </dt>
            <dd class="mt-1 break-words">
              {{ session.phone_number || '—' }}
            </dd>
          </div>
          <div>
            <dt class="font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.CONNECTED_ACCOUNT') }}
            </dt>
            <dd class="mt-1 break-words">
              {{ session.account_name || '—' }}
            </dd>
          </div>
          <div v-if="session.last_error" class="md:col-span-2">
            <dt class="font-medium text-n-slate-12">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.LAST_ERROR') }}
            </dt>
            <dd class="mt-1 break-words text-n-ruby-11">
              {{ session.last_error }}
            </dd>
          </div>
        </dl>

        <div
          v-if="session.status === 'awaiting_code'"
          class="mt-4 flex flex-col gap-3"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.CODE_LABEL') }}
            <input
              v-model="code"
              type="text"
              class="mt-2 w-full"
              :placeholder="
                $t('INBOX_MGMT.SETTINGS_POPUP.MAX.CODE_PLACEHOLDER')
              "
            />
          </label>
          <div class="flex justify-start">
            <Button
              size="sm"
              color="blue"
              :is-loading="isLoading"
              :label="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.SUBMIT_CODE_BUTTON')"
              @click="submitCode"
            />
          </div>
        </div>

        <div
          v-if="session.status === 'awaiting_password'"
          class="mt-4 flex flex-col gap-3"
        >
          <label class="text-sm font-medium text-n-slate-12">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.MAX.PASSWORD_LABEL') }}
            <input
              v-model="password"
              type="password"
              class="mt-2 w-full"
              :placeholder="
                $t('INBOX_MGMT.SETTINGS_POPUP.MAX.PASSWORD_PLACEHOLDER')
              "
            />
          </label>
          <div class="flex justify-start">
            <Button
              size="sm"
              color="blue"
              :is-loading="isLoading"
              :label="
                $t('INBOX_MGMT.SETTINGS_POPUP.MAX.SUBMIT_PASSWORD_BUTTON')
              "
              @click="submitPassword"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="mt-5 flex flex-wrap gap-3">
      <Button
        color="blue"
        :is-loading="isLoading"
        :label="primaryActionLabel"
        @click="
          session.status === 'connected'
            ? fetchSession()
            : fetchSession({ create: true })
        "
      />
      <Button
        v-if="showDisconnectButton"
        color="slate"
        :is-loading="isLoading"
        :label="$t('INBOX_MGMT.SETTINGS_POPUP.MAX.DISCONNECT_BUTTON')"
        @click="disconnectSession"
      />
    </div>
  </div>
</template>
